import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bookingActions, paymentActions } from '../../_actions';
import { paymentService } from '../../_services';
import { marketplaceService } from '../../_services/marketplace.service';
import { loadStripe } from '@stripe/stripe-js';
import siteSetting from '../../config/env';
import toast from '../shared/Toast';
import BookingManageFlows from './BookingManageFlows';
import ReviewModal from './ReviewModal';
import { setHeroHandoff, restRect } from './heroHandoff';
import {
  startOf, clientStartOf, fmtTime12, fmtShort,
  dayLabel, dayKey, monthLabel, monthKey, countdownLabel, sortByStart, groupBookings,
  servicesOf, serviceLabelOf, stylistNameOf, salonNameOf, thumbOf, avatarOf, moneyLineOf,
  isCancelled, listStatusOf, headlineOf, movedIds,
} from './bookingHelpers';
import { groupSeries, seriesIdOf, differenceSaid, rememberSeries } from './seriesHelpers';
import DepartedClientsCard from '../clients/departure/DepartedClientsCard';
import './dashboard.css';
import './ReviewModal.css';
import './BookingsList.css';

const stripePromise = loadStripe(siteSetting.stripeKey);

// Rows shown under Upcoming after the Next up card before "Show all". Keeps
// Past within reach for a client with a standing weekly booking.
const UPCOMING_CAP = 5;
const PAGE = 20;

const Chevron = () => (
  <span className="bl-chev" aria-hidden="true">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
  </span>
);

const Thumb = ({ src, initials, className }) => (
  <span className={className}>
    {src ? <img src={src} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : initials}
  </span>
);

const RepeatIcon = () => (
  <svg className="bl-repeat-ic" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8a5 5 0 0 1 8.5-3.5L13 6M13 3v3h-3M13 8a5 5 0 0 1-8.5 3.5L3 10M3 13v-3h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const Pill = ({ status }) => (status ? <span className={`bl-pill bl-pill--${status.tone}`}>{status.text}</span> : null);


class DashboardBookings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      loadingPast: false,
      upcomingError: false,
      pastError: false,
      loadingMore: { upcoming: false, past: false },
      expandedUpcoming: false,
      upcomingBookings: [],
      pastBookings: [],
      upcomingPagination: { skip: 0, limit: PAGE, total: 0, hasMore: false },
      pastPagination: { skip: 0, limit: PAGE, total: 0, hasMore: false },
      // Reschedule / cancel run in BookingManageFlows: { type, booking, nonce }
      manage: null,
      reviewModalOpen: false,
      reviewBooking: null,
      // Per-booking flag while the BE starts the Stripe Checkout session.
      confirmingBookings: {},
      // Headline image per salon id for the Next up card ('' once looked up
      // and missing, so the square profile image is used instead).
      heroImages: {},
      // List transitions: a row collapsing out of Upcoming after a cancel,
      // and the id that should fade in under Past once the refresh lands.
      leavingId: null,
      enteringId: null,
      // Repeats opened to show every visit
      openSeries: {},
    };
    // Rows moved this session glow once; remembered so a re-render does not replay it.
    this.glowed = new Set();
  }

  componentDidMount() {
    // If the user landed here from the Stripe Checkout return URL (card
    // capture confirm flow), finalise the booking before loading the lists.
    this.handleStripeReturn().finally(() => {
      this.loadAllBookings();
    });
    // The booking sheet sits over this list; when it cancels or moves a
    // booking it says so, and the list updates in place underneath.
    window.addEventListener('beu:bookings-changed', this.onBookingsChanged);
  }

  componentWillUnmount() {
    window.removeEventListener('beu:bookings-changed', this.onBookingsChanged);
    clearTimeout(this.leaveTimer);
  }

  // A cancelled booking visibly leaves Upcoming (fades and collapses) before
  // the lists refresh and it reappears under Past. Anything else refreshes
  // straight away.
  onBookingsChanged = (e) => {
    const d = (e && e.detail) || {};
    this.afterChange(d.id, d.type);
  };
  afterChange = (id, type) => {
    if (type === 'cancelled' && id && this.state.upcomingBookings.some((b) => b._id === id)) {
      this.setState({ leavingId: id });
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => {
        this.setState({ leavingId: null, enteringId: id });
        this.refresh();
      }, 220);
      return;
    }
    this.refresh();
  };

  /* ---------- Card capture confirm (Stripe Checkout) ---------- */

  /**
   * Finalise the card-capture confirmation when the user returns from
   * Stripe Checkout. Expected query params:
   *   ?s=s&bid=<bookingId>&tid=<transactionId>&session_id=<sessionId>  -> success
   *   ?s=c                                                             -> cancel
   * Clears the params from the URL either way so a refresh doesn't re-trigger.
   */
  handleStripeReturn = () => {
    const search = this.props.location && this.props.location.search;
    if (!search) return Promise.resolve();

    const qs = new URLSearchParams(search);
    const successKey = qs.get('s');
    if (!successKey) return Promise.resolve();

    const bid = qs.get('bid');
    const sessionId = qs.get('session_id');
    const clearUrl = () => {
      if (this.props.history) this.props.history.replace('/dashboard/bookings');
    };

    if (successKey === 'c' || successKey !== 's' || !bid || !sessionId) {
      toast.error('Booking not confirmed. Please try again.');
      clearUrl();
      return Promise.resolve();
    }

    return this.props.dispatch(paymentActions.makeCustomerPrimaryCard({ bookingId: bid, sessionId }))
      .then(() => { toast.success('Booking confirmed. Your card has been saved.'); })
      .catch(() => { toast.error('Booking not confirmed. Please try again.'); })
      .finally(() => { clearUrl(); });
  };

  /**
   * Start the card-capture confirm flow: ask the BE for a Stripe Checkout
   * session, then hand off to Stripe. The result lands in handleStripeReturn.
   */
  handleConfirmBooking = async (booking) => {
    if (!booking || !booking._id) return;
    const bookingId = booking._id;
    this.setState(prev => ({ confirmingBookings: { ...(prev.confirmingBookings || {}), [bookingId]: true } }));
    try {
      const stripe = await stripePromise;
      const response = await paymentService.bookingPayment({ bookingId, userConfirm: 1 });
      if (response && response.data && response.data.statusCode === 1) {
        const sessionId = response.data.responseData && response.data.responseData.result && response.data.responseData.result.id;
        if (!sessionId) {
          toast.error('Unable to start card confirmation. Please try again.');
          this.clearConfirming(bookingId);
          return;
        }
        const res = await stripe.redirectToCheckout({ sessionId });
        if (res && res.error) {
          toast.error(res.error.message || 'Stripe checkout failed.');
          this.clearConfirming(bookingId);
        }
      } else {
        const msg = response && response.data && ((response.data.error && response.data.error.responseMessage) || response.data.messages);
        toast.error(msg || 'Unable to start card confirmation. Please try again.');
        this.clearConfirming(bookingId);
      }
    } catch (err) {
      toast.error('Unable to start card confirmation. Please try again.');
      this.clearConfirming(bookingId);
    }
  };

  clearConfirming = (bookingId) => {
    this.setState(prev => ({ confirmingBookings: { ...(prev.confirmingBookings || {}), [bookingId]: false } }));
  };

  // A booking needs the client to confirm and add a card if the salon has
  // card capture enabled and no card has been saved against the booking yet.
  needsCardConfirmation = (booking) => {
    if (!booking) return false;
    const cardCaptureEnabled = booking.isCardCapture === 1 || booking.hairdresserCardCapture === 1;
    const hasCardSaved = !!(booking.stripePaymentMethodId && booking.stripePaymentMethodId !== '');
    return cardCaptureEnabled && !hasCardSaved;
  };

  /* ---------- Loading ---------- */

  // Upcoming first so the top of the page renders, then Past underneath.
  loadAllBookings = () => {
    this.setState({ loading: true, upcomingError: false, pastError: false, expandedUpcoming: false });
    return this.fetchPage('upcoming', 0)
      .then(() => this.ensureHero(this.nextUpOf(this.state.upcomingBookings)))
      .catch(() => this.setState({ upcomingError: true }))
      .finally(() => {
        this.setState({ loading: false, loadingPast: true });
        this.fetchPage('past', 0)
          .catch(() => this.setState({ pastError: true }))
          .finally(() => this.setState({ loadingPast: false }));
      });
  };

  // After a cancel or a move: reload both lists without the skeleton, so the
  // page updates in place instead of jumping to the top.
  refresh = () => {
    this.fetchPage('upcoming', 0).then(() => this.ensureHero(this.nextUpOf(this.state.upcomingBookings))).catch(() => {});
    this.fetchPage('past', 0).catch(() => {});
  };

  // The Next up card shows the salon's headline photo, which only the public
  // summary carries (the list has the square profile image). One request per
  // salon, awaited with the list so the card does not swap images on load.
  ensureHero = (booking) => {
    const sid = booking && booking.salonInfo && booking.salonInfo._id;
    if (!sid || this.state.heroImages[sid] !== undefined) return Promise.resolve();
    return marketplaceService.getSalonSummary({ salonId: sid })
      .then((res) => headlineOf(res && res.salon))
      .catch(() => '')
      .then((url) => this.setState((prev) => ({ heroImages: { ...prev.heroImages, [sid]: url || '' } })));
  };

  nextUpOf = (list) => {
    const now = Date.now();
    return sortByStart(list.filter((b) => !isCancelled(b))).find((b) => !this.isPurchaseBooking(b) && startOf(b).getTime() > now) || null;
  };

  fetchPage = (type, skip) => {
    const { dispatch } = this.props;
    const isUpcoming = type === 'upcoming';
    const params = { skip, limit: PAGE };
    const action = isUpcoming ? bookingActions.getClientUpcomingBookings(params) : bookingActions.getClientPastBookings(params);
    return dispatch(action).then((response) => {
      const data = (response && response.data && response.data.responseData) || null;
      if (!data || (response.data.statusCode === 0) || !Array.isArray(data.bookings)) throw new Error('bad response');
      const fresh = data.bookings;
      const pagination = data.pagination || { skip, limit: PAGE, total: fresh.length, hasMore: false };
      this.setState((prev) => {
        const listKey = isUpcoming ? 'upcomingBookings' : 'pastBookings';
        const pagKey = isUpcoming ? 'upcomingPagination' : 'pastPagination';
        const existing = skip > 0 ? prev[listKey] : [];
        const seen = new Set(existing.map((b) => b._id));
        return { [listKey]: [...existing, ...fresh.filter((b) => !seen.has(b._id))], [pagKey]: pagination };
      });
    });
  };

  loadMore = (type) => {
    const pag = type === 'upcoming' ? this.state.upcomingPagination : this.state.pastPagination;
    const list = type === 'upcoming' ? this.state.upcomingBookings : this.state.pastBookings;
    if (!pag.hasMore || this.state.loadingMore[type]) return;
    this.setState((prev) => ({ loadingMore: { ...prev.loadingMore, [type]: true } }));
    this.fetchPage(type, list.length)
      .catch(() => toast.error("Couldn't load more bookings. Please try again."))
      .finally(() => this.setState((prev) => ({ loadingMore: { ...prev.loadingMore, [type]: false } })));
  };

  /* ---------- Purchases (gift cards, product orders) ---------- */

  // Online purchases are written as service-less completed bookings so they
  // flow into finance. They are sales, not appointments, so they render as
  // plain rows with no details page behind them.
  isPurchaseBooking = (booking) => {
    if (servicesOf(booking).length > 0) return false;
    const products = booking.products || [];
    const vouchers = booking.vouchers || [];
    return products.length > 0 || vouchers.length > 0 || (booking.totalVoucherPrice || 0) > 0 || (booking.totalProductPrice || 0) > 0;
  };

  getPurchaseInfo = (booking) => {
    const products = booking.products || [];
    const vouchers = booking.vouchers || [];
    const currencySymbol = booking.currencySymbol || '£';
    const isGiftCard = vouchers.length > 0 || (booking.totalVoucherPrice || 0) > 0;
    if (isGiftCard) {
      const amount = booking.totalVoucherPrice || (vouchers[0] && vouchers[0].voucherPrice) || 0;
      return { title: 'Gift card', detail: `${currencySymbol}${Number(amount).toFixed(2)} gift card` };
    }
    const counts = {};
    products.forEach(p => { const name = p.productName || 'Product'; counts[name] = (counts[name] || 0) + 1; });
    const detail = Object.keys(counts).map(name => (counts[name] > 1 ? `${counts[name]}× ${name}` : name)).join(', ');
    return { title: 'Product order', detail: detail || 'Online order' };
  };

  /* ---------- Reviews, rebook, navigation ---------- */

  canReviewBooking = (booking) => {
    if (booking.hasHairdresserReview || booking.hasSalonReview) return false;
    if (typeof booking.canReview === 'boolean') return booking.canReview;
    return booking.bookingStatus === 4 || booking.status === 'completed';
  };

  handleReviewClick = (booking) => this.setState({ reviewModalOpen: true, reviewBooking: booking });
  closeReviewModal = () => this.setState({ reviewModalOpen: false, reviewBooking: null });
  handleReviewSubmitted = (bookingId) => {
    this.setState((prev) => ({
      pastBookings: prev.pastBookings.map((b) => (b._id === bookingId ? { ...b, canReview: false, hasHairdresserReview: true, hasSalonReview: true } : b)),
    }));
  };

  openManage = (type, booking) => this.setState({ manage: { type, booking, nonce: Date.now() } });
  closeManage = () => this.setState({ manage: null });
  // The single-booking endpoint carries no review flags, so the sheet learns
  // whether a review is possible from the list that opened it.
  openDetails = (booking) => {
    if (this.props.history) this.props.history.push(`/dashboard/bookings/${booking._id}`, { canReview: this.canReviewBooking(booking) });
  };

  // Book the same services with the same stylist again.
  handleRebookClick = (booking) => {
    const salonId = booking.salonId || (booking.salonInfo && booking.salonInfo._id);
    const hairdresserId = booking.hairdresserId || (booking.hairdresserInfo && booking.hairdresserInfo._id);
    if (!salonId || !hairdresserId) { toast.error('Unable to rebook this appointment'); return; }
    const serviceIds = servicesOf(booking).map(s => s.serviceId).filter(Boolean);
    let url = `/salon/${salonId}/professional/${hairdresserId}`;
    if (serviceIds.length > 0) url += `?rebookServiceIds=${serviceIds.join(',')}`;
    this.props.history.push(url);
  };

  /* ---------- Render pieces ---------- */

  linkProps = (booking) => ({
    role: 'link',
    tabIndex: 0,
    onClick: () => this.openDetails(booking),
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openDetails(booking); } },
  });

  renderNextUp = (booking) => {
    const start = clientStartOf(booking);
    const needsConfirm = this.needsCardConfirmation(booking);
    const pending = booking.bookingStatus === 0 || booking.status === 'pending';
    const canManage = !isCancelled(booking) && startOf(booking).getTime() > Date.now();
    const confirming = !!(this.state.confirmingBookings && this.state.confirmingBookings[booking._id]);
    const thumb = thumbOf(booking);
    const sid = booking.salonInfo && booking.salonInfo._id;
    const heroSrc = (sid && this.state.heroImages[sid]) || thumb.src;
    const avatar = avatarOf(booking);
    const stylist = stylistNameOf(booking);
    const salon = salonNameOf(booking);
    const stop = (e) => e.stopPropagation();
    // The sheet grows this photo from where it sits into its own hero.
    const open = (e) => {
      const img = e.currentTarget.querySelector('.bl-hero-img img') || e.currentTarget.querySelector('.bl-hero-img');
      if (img && heroSrc) setHeroHandoff({ id: booking._id, src: heroSrc, rect: restRect(img) });
      this.openDetails(booking);
    };
    return (
      <article
        className="bl-hero"
        data-booking-id={booking._id}
        aria-label={`${serviceLabelOf(booking)} with ${stylist || 'your stylist'}, ${dayLabel(start)} ${fmtTime12(start)}. View booking`}
        role="link" tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e); } }}
      >
        <div className={`bl-hero-img${heroSrc ? '' : ' bl-hero-img--empty'}`}>
          {heroSrc && <img src={heroSrc} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
          <span className="bl-hero-chip">{pending ? 'Waiting for the salon' : countdownLabel(start)}</span>
          <Thumb className="bl-avatar" src={avatar.src} initials={avatar.initials} />
        </div>
        <div className="bl-hero-body">
          <div className="bl-hero-when">{dayLabel(start)}, {fmtTime12(start)}</div>
          <div className="bl-hero-what">{serviceLabelOf(booking)}</div>
          {(stylist || salon) && <div className="bl-hero-who">{[stylist, salon].filter(Boolean).join(' · ')}</div>}
          {moneyLineOf(booking) && <div className="bl-hero-money">{moneyLineOf(booking)}</div>}
          {this.seriesNote(booking)}
          {canManage && (
            <div className="bl-hero-cta" onClick={stop} onKeyDown={stop}>
              <button type="button" className="bl-btn" onClick={() => this.openManage('cancel', booking)}>Cancel</button>
              {needsConfirm ? (
                <button type="button" className="bl-btn bl-btn--primary" onClick={() => this.handleConfirmBooking(booking)} disabled={confirming}>
                  {confirming ? 'Opening…' : 'Confirm booking'}
                </button>
              ) : !pending && (
                <button type="button" className="bl-btn bl-btn--primary" onClick={() => this.openManage('reschedule', booking)}>Reschedule</button>
              )}
            </div>
          )}
        </div>
      </article>
    );
  };

  // A visit of a repeat: which repeat, and whether it's different from usual
  seriesInfo = (booking) => {
    const id = seriesIdOf(booking);
    const series = id && this._series ? this._series.find(x => x.id === id) : null;
    if (!series) return null;
    const diff = series.different.find(x => x.visit._id === booking._id);
    return { series, diffs: diff ? diff.diffs : [] };
  };

  seriesNote = (booking) => {
    const info = this.seriesInfo(booking);
    if (!info) return null;
    return (
      <>
        <div className="bl-hero-who bl-repeat-line"><RepeatIcon />{info.series.said}</div>
        {info.diffs.length > 0 && <div className="bl-diff" role="note">{differenceSaid(info.diffs)}</div>}
      </>
    );
  };

  // One row for a repeat. Visits that are different from usual are listed
  // on it, so they're seen without opening it; tapping shows every visit.
  renderSeries = (series, nextUp, hasMore) => {
    const visits = series.visits.filter(v => v !== nextUp);
    if (!visits.length) return null;
    const first = visits[0];
    const thumb = thumbOf(first);
    const open = !!this.state.openSeries[series.id];
    const stylist = stylistNameOf(first);
    const salon = salonNameOf(first);
    const different = series.different.filter(x => x.visit !== nextUp);
    const toggle = () => this.setState(st => ({ openSeries: { ...st.openSeries, [series.id]: !st.openSeries[series.id] } }));
    const start = clientStartOf(first);
    return (
      <div key={series.id} className={`bl-series${open ? ' is-open' : ''}`}>
        <button type="button" className="bl-series-head" aria-expanded={open} onClick={toggle}>
          <Thumb className="bl-thumb" src={thumb.src} initials={thumb.initials} />
          <span className="bl-row-main">
            <span className="bl-row-time bl-repeat-line"><RepeatIcon />{series.said}</span>
            <span className="bl-row-what">{serviceLabelOf(first)}</span>
            <span className="bl-row-who">{[stylist, salon].filter(Boolean).join(' · ')}</span>
            <span className="bl-row-who">Next {dayLabel(start).toLowerCase() === 'today' || dayLabel(start).toLowerCase() === 'tomorrow' ? dayLabel(start).toLowerCase() : `on ${dayLabel(start)}`} at {fmtTime12(start)} · {visits.length}{hasMore ? '+' : ''} more booked</span>
          </span>
          <span className="bl-row-side"><span className={`bl-series-chev${open ? ' is-open' : ''}`}><Chevron /></span></span>
        </button>
        {different.length > 0 && (
          <ul className="bl-series-diffs" aria-label="Visits that are different from usual">
            {different.slice(0, 3).map(({ visit, diffs }) => (
              <li key={visit._id}>
                <button type="button" className="bl-diff bl-diff--link" onClick={() => this.openDetails(visit)}>
                  <b>{fmtShort(clientStartOf(visit))}</b> {diffs.join(', ')}
                </button>
              </li>
            ))}
            {different.length > 3 && <li className="bl-series-more">and {different.length - 3} more different visit{different.length - 3 === 1 ? '' : 's'}, shown below</li>}
          </ul>
        )}
        {open && (
          <div className="bl-series-visits">
            {visits.map(v => this.renderRow(v, { inSeries: true }))}
            {hasMore && <p className="bl-series-more">Later visits load as they come up.</p>}
          </div>
        )}
      </div>
    );
  };

  renderRow = (booking, { past = false, inSeries = false } = {}) => {
    const thumb = thumbOf(booking);
    const start = clientStartOf(booking);
    if (this.isPurchaseBooking(booking)) {
      const info = this.getPurchaseInfo(booking);
      const d = new Date(booking.bookingDate);
      return (
        <div key={booking._id} className={`bl-row bl-row--static${past ? ' bl-row--past' : ''}`}>
          <Thumb className="bl-thumb" src={thumb.src} initials={info.title === 'Gift card' ? 'GC' : 'PO'} />
          <div className="bl-row-main">
            <div className="bl-row-time">{d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
            <div className="bl-row-what">{info.title}</div>
            <div className="bl-row-who">{[info.detail, salonNameOf(booking)].filter(Boolean).join(' · ')}</div>
          </div>
          <div className="bl-row-side"><Pill status={listStatusOf(booking, { past: true })} /></div>
        </div>
      );
    }
    const seriesDiff = !past ? this.seriesInfo(booking) : null;
    const different = !!(seriesDiff && seriesDiff.diffs.length);
    const status = different ? { tone: 'amber', text: differenceSaid(seriesDiff.diffs).split(':')[0] } : listStatusOf(booking, { past, needsConfirm: this.needsCardConfirmation(booking) });
    const stylist = stylistNameOf(booking);
    const salon = salonNameOf(booking);
    const canReview = past && this.canReviewBooking(booking);
    const timeText = past || inSeries ? fmtShort(start) : fmtTime12(start);
    const leaving = this.state.leavingId === booking._id;
    const entering = this.state.enteringId === booking._id;
    let glow = false;
    if (!past && movedIds().includes(String(booking._id)) && !this.glowed.has(booking._id)) { glow = true; this.glowed.add(booking._id); }
    return (
      <div
        key={booking._id}
        className={`bl-row${past ? ' bl-row--past' : ''}${canReview ? ' bl-row--has-action' : ''}${leaving ? ' bl-row--leaving' : ''}${entering ? ' bl-row--entering' : ''}${glow ? ' bl-row--glow' : ''}`}
        aria-label={`${serviceLabelOf(booking)} with ${stylist || 'your stylist'}, ${past ? '' : `${dayLabel(start)} `}${timeText}${status ? `, ${status.text}` : ''}. View booking`}
        {...this.linkProps(booking)}
      >
        <Thumb className="bl-thumb" src={thumb.src} initials={thumb.initials} />
        <div className="bl-row-main">
          <div className="bl-row-time">{timeText}</div>
          <div className="bl-row-what">{serviceLabelOf(booking)}</div>
          {(stylist || salon) && <div className="bl-row-who">{[stylist, salon].filter(Boolean).join(' · ')}</div>}
          {different && <div className="bl-row-who bl-row-diff">{seriesDiff.diffs.join(', ')}</div>}
        </div>
        <div className="bl-row-side">
          {!canReview && <Pill status={status} />}
          <Chevron />
        </div>
        {canReview && (
          <button type="button" className="bl-row-action" onClick={(e) => { e.stopPropagation(); this.handleReviewClick(booking); }} onKeyDown={(e) => e.stopPropagation()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9-4.3-4.1 5.9-.8z" /></svg>
            <span>Leave a review</span>
          </button>
        )}
      </div>
    );
  };

  renderGroups = (groups, opts) => groups.map((g) => (
    <div key={g.key} className="bl-group">
      <h2 className="bl-h">{g.label}</h2>
      {g.items.map((b) => this.renderRow(b, opts))}
    </div>
  ));

  renderSkeletonRows = (n) => Array.from({ length: n }).map((_, i) => (
    <div key={i} className="bl-skel-row">
      <div className="bl-skel bl-skel--thumb" />
      <div>
        <div className="bl-skel" style={{ width: '40%', height: 14 }} />
        <div className="bl-skel" style={{ width: '85%', height: 12, marginTop: 8 }} />
        <div className="bl-skel" style={{ width: '60%', height: 11, marginTop: 6 }} />
      </div>
      <div className="bl-skel bl-skel--pill" />
    </div>
  ));

  renderSkeleton = () => (
    <div aria-busy="true" aria-label="Loading your bookings">
      <div className="bl-skel bl-skel--h" style={{ marginTop: 0 }} />
      <div className="bl-hero" style={{ cursor: 'default' }}>
        <div className="bl-skel bl-skel--img" />
        <div className="bl-hero-body">
          <div className="bl-skel" style={{ width: '55%', height: 20 }} />
          <div className="bl-skel" style={{ width: '80%', height: 14, marginTop: 10 }} />
          <div className="bl-skel" style={{ width: '50%', height: 12, marginTop: 8 }} />
          <div className="bl-hero-cta"><div className="bl-skel" style={{ flex: 1, height: 44, borderRadius: 10 }} /><div className="bl-skel" style={{ flex: 1, height: 44, borderRadius: 10 }} /></div>
        </div>
      </div>
      <div className="bl-skel bl-skel--h" />
      {this.renderSkeletonRows(2)}
      <div className="bl-skel bl-skel--h" />
      {this.renderSkeletonRows(2)}
    </div>
  );

  renderFailure = (what) => (
    <div className="bl-note" role="alert">
      <h3>Couldn't load your {what}</h3>
      <p>Check your connection and try again.</p>
      <div className="bl-hero-cta">
        <button type="button" className="bl-btn bl-btn--primary" onClick={this.loadAllBookings}>Retry</button>
      </div>
    </div>
  );

  renderEmptyUpcoming = (lastPast) => (
    <div className="bl-note">
      <h3>Nothing booked yet</h3>
      <p>{lastPast ? 'Your next appointment will show here.' : 'Find a salon or stylist and your appointments will show here.'}</p>
      <div className="bl-hero-cta">
        {lastPast ? (
          <>
            <Link to="/" className="bl-btn">Explore</Link>
            <button type="button" className="bl-btn bl-btn--primary" onClick={() => this.handleRebookClick(lastPast)}>Book again</button>
          </>
        ) : (
          <Link to="/" className="bl-btn bl-btn--primary">Book now</Link>
        )}
      </div>
    </div>
  );

  render() {
    const {
      loading, loadingPast, upcomingError, pastError, loadingMore, expandedUpcoming,
      upcomingBookings, pastBookings, upcomingPagination, pastPagination,
    } = this.state;

    // Cancelled bookings never sit under Upcoming: they join Past under their
    // date, so the Next up card is always something real.
    const live = sortByStart(upcomingBookings.filter((b) => !isCancelled(b)));
    const cancelledUpcoming = upcomingBookings.filter(isCancelled);
    const nextUp = this.nextUpOf(upcomingBookings);
    // Repeats: one row each (with the visits that are different called out),
    // and the Next up card says when it's part of one
    const { series } = groupSeries(live);
    this._series = series;
    rememberSeries(series);
    const inSeries = new Set(series.flatMap(x => x.visits));
    const rest = live.filter((b) => b !== nextUp && !inSeries.has(b));
    const visibleRest = expandedUpcoming ? rest : rest.slice(0, UPCOMING_CAP);
    const hasHidden = rest.length > UPCOMING_CAP;
    const upcomingGroups = groupBookings(visibleRest, dayKey, dayLabel);

    const pastAll = sortByStart([...cancelledUpcoming, ...pastBookings], -1);
    const pastGroups = groupBookings(pastAll, monthKey, monthLabel);
    const lastPast = pastAll.find((b) => !this.isPurchaseBooking(b) && !isCancelled(b)) || null;
    const hasUpcoming = live.length > 0;

    return (
      <div className="dashboard-fade-in bl-page">
        <div className="dashboard-page-header">
          <div className="dashboard-page-header-text">
            <h1 className="dashboard-page-title">Bookings</h1>
          </div>
        </div>

        {/* A self-employed stylist who has left their salon lands here: their
            clients from it, to download (renders nothing for anyone else). */}
        <DepartedClientsCard mode="account" />

        {loading ? this.renderSkeleton() : (
          <>
            <section className="bl-section" aria-label="Upcoming bookings">
              {upcomingError ? this.renderFailure('upcoming bookings') : !hasUpcoming ? this.renderEmptyUpcoming(lastPast) : (
                <>
                  {nextUp && <h2 className="bl-h">Next up</h2>}
                  {nextUp && <div key={nextUp._id} className="bl-hero-swap">{this.renderNextUp(nextUp)}</div>}
                  {series.some(x => x.visits.some(v => v !== nextUp)) && (
                    <div className="bl-group">
                      <h2 className="bl-h">{series.length === 1 ? 'Repeat booking' : 'Repeat bookings'}</h2>
                      {series.map(x => this.renderSeries(x, nextUp, upcomingPagination.hasMore))}
                    </div>
                  )}
                  {this.renderGroups(upcomingGroups)}
                  {hasHidden && (
                    <button type="button" className="bl-more" onClick={() => this.setState({ expandedUpcoming: !expandedUpcoming })}>
                      {expandedUpcoming ? 'Show less' : `Show all ${rest.length + (nextUp && !inSeries.has(nextUp) ? 1 : 0)} upcoming`}
                    </button>
                  )}
                  {expandedUpcoming && upcomingPagination.hasMore && (
                    <button type="button" className="bl-more" onClick={() => this.loadMore('upcoming')} disabled={loadingMore.upcoming}>
                      {loadingMore.upcoming ? 'Loading…' : 'Show more'}
                    </button>
                  )}
                </>
              )}
            </section>

            {(loadingPast || pastError || pastGroups.length > 0) && (
              <section className="bl-section" aria-label="Past bookings">
                <h2 className="bl-past-title">Past</h2>
                {loadingPast ? this.renderSkeletonRows(3) : pastError ? this.renderFailure('past bookings') : (
                  <>
                    {this.renderGroups(pastGroups, { past: true })}
                    {pastPagination.hasMore && (
                      <button type="button" className="bl-more" onClick={() => this.loadMore('past')} disabled={loadingMore.past}>
                        {loadingMore.past ? 'Loading…' : 'Show earlier bookings'}
                      </button>
                    )}
                  </>
                )}
              </section>
            )}

            {/* Reschedule and cancel: the same sheets the details page uses */}
            <BookingManageFlows request={this.state.manage} onClose={this.closeManage} onCancelled={(id, type) => this.afterChange(id, type)} history={this.props.history} />

            <ReviewModal
              isOpen={this.state.reviewModalOpen}
              booking={this.state.reviewBooking}
              onClose={this.closeReviewModal}
              onReviewSubmitted={this.handleReviewSubmitted}
            />
          </>
        )}
      </div>
    );
  }
}

export default connect()(DashboardBookings);
